import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Batch, BatchRepositoryService, BatchSummary, BatchSummaryRepositoryService, Bundle, BundlesRepositoryService, ConfigurationState, eBatchState, OrderMetaDataRepositoryService, BatchStateRepositoryService, BatchStateForUpdate, ConfirmDialogComponent } from 'reg-hub-common';
import { catchError, Subject, takeUntil, tap } from 'rxjs';
import { BatchesHubService } from '../../services/hubs/batches-hub/batches-hub.service';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-batch',
    templateUrl: './batch.component.html',
    styleUrl: './batch.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BatchComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  bundleID: string = '';
  bundle: Bundle | null = null;
  batchID: string = '';
  batch: Batch | null = null;
  batchSummary: BatchSummary | null = null;
  stagedProgress: number = 0;
  processedProgress: number = 0;
  submittedProgress: number = 0;
  completedProgress: number = 0;
  totalProgress: number = 0;
  showNormalizedDocument: boolean = false;
  requestingUser: string = "";
  private unsubscribe$ = new Subject<void>();

  constructor(private activatedRoute: ActivatedRoute,
    private bundleRepositoryService: BundlesRepositoryService,
    private batchRepositoryService: BatchRepositoryService,
    private batchSummaryRepositoryService: BatchSummaryRepositoryService,
    private batchStateService: BatchStateRepositoryService,
    private orderMetaDataService: OrderMetaDataRepositoryService,
    private configurationState: ConfigurationState,
    protected batchesHub: BatchesHubService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe(params => {
      this.bundleID = params.get('bundleID')!;
      this.batchID = params.get('batchID')!;
      this.getBundle();
      this.getBatch();
      this.getBatchSummary();
    });

    
  }

  ngOnDestroy(): void {
    // Signal all subscriptions to complete
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getBundle() {
    this.configurationState.getCorporationID().then(corporationID => {
      this.bundleRepositoryService.populateBaseLink([corporationID]);
      this.bundleRepositoryService.getBundle(this.bundleID).subscribe(bundle => {
        this.bundle = bundle;
      })
    })
  }

  getBatch() {
    this.batchRepositoryService.getBatch(this.bundleID, this.batchID).subscribe(batch => {
      this.batch = batch;
      this.showNormalizedDocument = this.batch.normalizedDocument?.id != this.batch.rawDocument?.id;
      this.getRequestingUser(batch);
    })
  }

  getRequestingUser(batch: Batch) {
    this.orderMetaDataService
      .getOrderMetaData(batch.corporationID, null, batch.userID, null).subscribe(data => {
        this.requestingUser = data.requestingUserFullName ?? "";
      })
  }

  getBatchSummary() {
    this.batchSummaryRepositoryService.getBatchSummary(this.bundleID, this.batchID).subscribe(summary => {
      this.batchSummary = summary;
      this.calculateProgress();
      this.subscribeToHub();
    })
  }

  calculateProgress() {
    if (this.batchSummary && this.batchSummary.totalOrders > 0) {
      this.stagedProgress = (this.batchSummary.stagedOrders / this.batchSummary.totalOrders) * 100;
      this.processedProgress = (this.batchSummary.processedOrders / this.batchSummary.totalOrders) * 100;
      this.submittedProgress = (this.batchSummary.submittedOrders / this.batchSummary.totalOrders) * 100;
      this.completedProgress = (this.batchSummary.completedOrders / this.batchSummary.totalOrders) * 100;
      this.totalProgress = ((this.batchSummary.stagedOrders + this.batchSummary.submittedOrders + this.batchSummary.completedOrders) / (this.batchSummary.totalOrders * 3));
    }
  }

  subscribeToHub() {
    this.batchesHub.connect();

    this.batchesHub.joinAllBatchGroups(this.batchID);

    this.batchesHub.batchState$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(batch => {
        if (this.batch) {
          this.batch.batchState = batch.batchState;
          this.batch.batchStateID = batch.batchStateID;
        }

        if (this.batchSummary) {
          this.batchSummary.batchStateID = batch.batchStateID;
        }
      });

    this.batchesHub.batchOrderCreated$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(batchOrder => {
        if (this.batchSummary) {
          this.batchSummary.totalOrders++;
          this.calculateProgress();
        }
      });

    this.batchesHub.batchOrderStaged$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(batchOrder => {
        if (this.batchSummary) {
          if (batchOrder.isStaged) {
            this.batchSummary.stagedOrders++;
            if (this.batchSummary.stagedOrders > this.batchSummary.totalOrders) {
              this.batchSummary.totalOrders = this.batchSummary.stagedOrders;
            }
          }
          else {
            this.batchSummary.stagedOrders--;
          }
          this.calculateProgress();
        }
      });

    this.batchesHub.batchOrderProcessed$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(batchOrder => {
        if (this.batchSummary) {
          if (batchOrder.isProcessed) {
            this.batchSummary.processedOrders++;
          }
          else {
            this.batchSummary.processedOrders--;
          }
          this.calculateProgress();
        }
      });

    this.batchesHub.batchOrderSubmitted$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(batchOrder => {
        if (this.batchSummary) {
          if (batchOrder.isSubmitted) {
            this.batchSummary.submittedOrders++;
          }
          else {
            this.batchSummary.submittedOrders--;
          }
          this.calculateProgress();
        }
      });

    this.batchesHub.batchOrderCompleted$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(batchOrder => {
        if (this.batchSummary) {
          if (batchOrder.isCompleted) {
            this.batchSummary.completedOrders++;
          }
          else {
            this.batchSummary.completedOrders--;
          }
          this.calculateProgress();
        }
      });
  }

  showCompleteBatch(): boolean {
    //If Batch is in Audit state and there are no more pending orders, give them the option to set batch as complete
    return (this.batchSummary?.batchStateID == eBatchState.audit && 
      this.batchSummary?.pendingOrders == 0);
  }

  completeBatchConfirm() {
    const dialogRef =this.dialog.open(ConfirmDialogComponent, {
      data: {
          title: this.transloco.translate('bundles.completeBatch'),
          confirmationPrompt: this.transloco.translate('bundles.confirmMarkThisBatchCompleteThis'),
          positiveResponseLabel: this.transloco.translate('bundles.complete'),
          negativeResponseLabel: this.transloco.translate('common.buttons.cancel')
      },
      minWidth: '800px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // submission is confirmed, submit batch
        this.completeBatch();
      }
    });
  }

  completeBatch() {
    const newOrderState: BatchStateForUpdate = {
      newState: eBatchState.finalizing
    }

    this.batchStateService.updateBatchState(this.batch!.bundleID, this.batch!.id, newOrderState)
      .pipe(
        tap(_ => {
          // Success handling
          this.openSnackBar(this.transloco.translate('common.messages.batchStateChangedSuccessfully'));
          this.batchSummary!.batchStateID = eBatchState.finalizing;
          this.batch!.batchStateID = eBatchState.finalizing;
          this.batch!.batchState = {
            id: eBatchState.finalizing,
            name: "Finalizing"
          }
        }),
        catchError(error => {
          // Error handling
          const errorResponse = error as HttpErrorResponse

          console.error('Error:', errorResponse);
          this.openSnackBar(this.transloco.translate('common.messages.failedToChangeBatchState'));

          return error; // or handle the error and return a safe value
        })
      )
      .subscribe();
  }

  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.duration = 5000; // Set the duration in milliseconds (2 seconds)

    this.snackBar.open(message, this.transloco.translate('common.buttons.close'), config);
  }
}
